// CPU mirror of the atmosphere model (km units), used for light colours.

const RAY_S = [5.802e-3, 13.558e-3, 33.1e-3];
const MIE_S = 3.996e-3;
const MIE_E = 4.4e-3;
const OZ_A = [0.65e-3, 1.881e-3, 0.085e-3];
const PLANET_R = 6360;
const ATMO_R = 6460;

function extinction(h: number, mieScale: number, out: number[]): void {
  const r = Math.exp(-h / 8);
  const m = MIE_E * mieScale * Math.exp(-h / 1.2);
  const o = Math.max(0, 1 - Math.abs(h - 25) / 15);
  out[0] = RAY_S[0]! * r + m + OZ_A[0]! * o;
  out[1] = RAY_S[1]! * r + m + OZ_A[1]! * o;
  out[2] = RAY_S[2]! * r + m + OZ_A[2]! * o;
}

const ext = [0, 0, 0];

/** Transmittance from altitude h (km) toward direction with cosine mu. */
export function transmittance(h: number, mu: number, mieScale: number, out: number[]): number[] {
  const ro = PLANET_R + h;
  // Hits the ground?
  const b = ro * mu;
  const cGround = ro * ro - PLANET_R * PLANET_R;
  if (mu < 0 && b * b - cGround >= 0) {
    // Soften the terminator slightly so light fades rather than snaps.
    const soft = Math.max(0, 1 + mu * 60);
    const t = transmittance(h, 0.0005, mieScale, out);
    out[0] = t[0]! * soft;
    out[1] = t[1]! * soft;
    out[2] = t[2]! * soft;
    return out;
  }
  const c = ro * ro - ATMO_R * ATMO_R;
  const tMax = -b + Math.sqrt(b * b - c);
  const n = 64;
  const dt = tMax / n;
  let o0 = 0, o1 = 0, o2 = 0;
  const sx = Math.sqrt(Math.max(0, 1 - mu * mu));
  for (let i = 0; i < n; i++) {
    const t = (i + 0.5) * dt;
    const px = sx * t, py = ro + mu * t;
    extinction(Math.hypot(px, py) - PLANET_R, mieScale, ext);
    o0 += ext[0]! * dt;
    o1 += ext[1]! * dt;
    o2 += ext[2]! * dt;
  }
  out[0] = Math.exp(-o0);
  out[1] = Math.exp(-o1);
  out[2] = Math.exp(-o2);
  return out;
}

const tSun = [0, 0, 0];

/**
 * Approximate sky irradiance on a horizontal surface at sea level (per unit
 * of sun illuminance), by integrating single scattering over a few directions.
 */
export function skyIrradiance(sunY: number, mieScale: number, out: number[]): number[] {
  out[0] = out[1] = out[2] = 0;
  const dirs: [number, number, number, number][] = [];
  // (x, y, z, weight) over the upper hemisphere, cosine weighted.
  for (const el of [0.12, 0.45, 0.9, 1.45]) {
    for (let a = 0; a < 6; a++) {
      const az = (a / 6) * Math.PI * 2;
      dirs.push([Math.cos(el) * Math.cos(az), Math.sin(el), Math.cos(el) * Math.sin(az), Math.sin(el) * Math.cos(el)]);
    }
  }
  const sx = Math.sqrt(Math.max(0, 1 - sunY * sunY));
  let wsum = 0;
  for (const [dx, dy, dz, w] of dirs) {
    const mu = dx * sx + dy * sunY;
    const pr = (3 / (16 * Math.PI)) * (1 + mu * mu);
    const g = 0.8;
    const pm = ((3 / (8 * Math.PI)) * ((1 - g * g) * (1 + mu * mu))) / ((2 + g * g) * Math.pow(1 + g * g - 2 * g * mu, 1.5));
    const n = 12;
    const tMax = Math.min(-(PLANET_R * dy) + Math.sqrt(PLANET_R * PLANET_R * dy * dy + ATMO_R * ATMO_R - PLANET_R * PLANET_R), 400);
    let T0 = 1, T1 = 1, T2 = 1;
    let L0 = 0, L1 = 0, L2 = 0;
    let tPrev = 0;
    for (let i = 0; i < n; i++) {
      const f = (i + 0.5) / n;
      const t = tMax * f * f;
      const d = t - tPrev;
      tPrev = t;
      const px = dx * t, py = PLANET_R + dy * t, pz = dz * t;
      const r = Math.hypot(px, py, pz);
      const h = r - PLANET_R;
      const muS = (px * sx + py * sunY) / r;
      transmittance(h, muS, mieScale, tSun);
      extinction(h, mieScale, ext);
      const rd = Math.exp(-h / 8), md = MIE_S * mieScale * Math.exp(-h / 1.2);
      for (let c = 0; c < 3; c++) {
        const s = (RAY_S[c]! * rd * pr + md * pm) * tSun[c]! + (RAY_S[c]! * rd + md) * tSun[c]! * 0.09;
        const T = c === 0 ? T0 : c === 1 ? T1 : T2;
        const sT = Math.exp(-ext[c]! * d);
        const add = (T * (s - s * sT)) / Math.max(ext[c]!, 1e-7);
        if (c === 0) { L0 += add; T0 *= sT; }
        else if (c === 1) { L1 += add; T1 *= sT; }
        else { L2 += add; T2 *= sT; }
      }
    }
    out[0]! += L0 * w;
    out[1]! += L1 * w;
    out[2]! += L2 * w;
    wsum += w;
  }
  // Cosine-weighted mean radiance times PI = irradiance.
  out[0] = (out[0]! / wsum) * Math.PI;
  out[1] = (out[1]! / wsum) * Math.PI;
  out[2] = (out[2]! / wsum) * Math.PI;
  return out;
}
